/**
 * The "storefront and founder" block shared by BrandEditor's Create and Edit
 * modals: Shopify storefront flag, ecommerce platform, Instagram URL and the
 * founder. Until this existed the four fields could only reach a brand through
 * an external-import seed — the form itself never wrote them, so they could not
 * be set, corrected or cleared by hand.
 *
 * Like the location block, the form state is one object so both modals hold,
 * reset, seed and save it the same way.
 */
import { useId } from 'react';
import type { WatchBrand } from '../../types';

export interface BrandCommerceForm {
  /**
   * `undefined` means "never stated", which is not the same as `false`: an
   * untouched brand must not have `isShopifyBrand: false` written onto it.
   */
  isShopifyBrand: boolean | undefined;
  ecommercePlatform: string;
  instagramUrl: string;
  founderName: string;
  /** Tri-state for the same reason as `isShopifyBrand`. */
  founderManaged: boolean | undefined;
  /**
   * Upstream "last founder edit" timestamp. Display-only — there is nothing
   * for a curator to type — but it lives in the form so a save that rewrites
   * `founder` carries it along instead of dropping it.
   */
  founderLastEditAt: string;
}

export const emptyBrandCommerceForm: BrandCommerceForm = {
  isShopifyBrand: undefined,
  ecommercePlatform: '',
  instagramUrl: '',
  founderName: '',
  founderManaged: undefined,
  founderLastEditAt: '',
};

export function brandCommerceFormFromBrand(brand: Partial<WatchBrand>): BrandCommerceForm {
  return {
    isShopifyBrand: brand.isShopifyBrand,
    ecommercePlatform: brand.ecommercePlatform ?? '',
    instagramUrl: brand.instagramUrl ?? '',
    founderName: brand.founder?.name ?? '',
    founderManaged: brand.founder?.managed,
    founderLastEditAt: brand.founder?.lastEditAt ?? '',
  };
}

/**
 * Lay an external-import seed's values over a form. Only what the seed carries
 * is touched, so a seed that ticked just the founder's name leaves the stored
 * `managed` flag — and everything else — as it was.
 */
export function applySeedToCommerceForm(
  form: BrandCommerceForm,
  values: Partial<WatchBrand>,
): BrandCommerceForm {
  const next = { ...form };
  if (values.isShopifyBrand !== undefined) next.isShopifyBrand = values.isShopifyBrand;
  if (values.ecommercePlatform !== undefined) next.ecommercePlatform = values.ecommercePlatform;
  if (values.instagramUrl !== undefined) next.instagramUrl = values.instagramUrl;
  if (values.founder?.name !== undefined) next.founderName = values.founder.name;
  if (values.founder?.managed !== undefined) next.founderManaged = values.founder.managed;
  if (values.founder?.lastEditAt !== undefined) next.founderLastEditAt = values.founder.lastEditAt;
  return next;
}

/**
 * The stored form of a platform name. Consumers compare it exactly
 * (`=== 'shopify'`), and the upstream column mixes "Shopify" with "shopify", so
 * it is folded to lower case on the way in.
 */
export function normaliseEcommercePlatform(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

/** The `founder` object the form describes, or undefined when it is empty. */
function founderFromForm(form: BrandCommerceForm): WatchBrand['founder'] {
  const founder: NonNullable<WatchBrand['founder']> = {};
  const name = form.founderName.trim();
  if (name) founder.name = name;
  if (form.founderManaged !== undefined) founder.managed = form.founderManaged;
  if (form.founderLastEditAt) founder.lastEditAt = form.founderLastEditAt;
  return Object.keys(founder).length > 0 ? founder : undefined;
}

/** What a NEW brand writes: only what was stated; nothing blank. */
export function brandCommerceCreatePayload(form: BrandCommerceForm): Partial<WatchBrand> {
  const payload: Partial<WatchBrand> = {};
  if (form.isShopifyBrand !== undefined) payload.isShopifyBrand = form.isShopifyBrand;
  const platform = normaliseEcommercePlatform(form.ecommercePlatform);
  if (platform) payload.ecommercePlatform = platform;
  const instagram = form.instagramUrl.trim();
  if (instagram) payload.instagramUrl = instagram;
  const founder = founderFromForm(form);
  if (founder) payload.founder = founder;
  return payload;
}

export interface BrandCommerceUpdate {
  payload: Partial<WatchBrand>;
  /**
   * The founder was emptied on a brand that had one. `founder` is a map, so it
   * cannot be cleared with '' like a string; the caller deletes the field.
   */
  clearFounder: boolean;
}

/**
 * What an EDIT writes. A blanked string that held a value is written as '' so
 * the clear persists (`undefined` would be stripped and keep the old value); a
 * field that was never set and is still blank is left off the document.
 * `founder` is written whole — `updateDoc` replaces a map, it does not merge.
 */
export function brandCommerceUpdatePayload(
  form: BrandCommerceForm,
  existing: WatchBrand,
): BrandCommerceUpdate {
  const payload: Partial<WatchBrand> = {};
  if (form.isShopifyBrand !== undefined) payload.isShopifyBrand = form.isShopifyBrand;

  const platform = normaliseEcommercePlatform(form.ecommercePlatform);
  if (platform) payload.ecommercePlatform = platform;
  else if (existing.ecommercePlatform) payload.ecommercePlatform = '';

  const instagram = form.instagramUrl.trim();
  if (instagram) payload.instagramUrl = instagram;
  else if (existing.instagramUrl) payload.instagramUrl = '';

  const founder = founderFromForm(form);
  if (founder) payload.founder = founder;
  return { payload, clearFounder: !founder && existing.founder !== undefined };
}

interface BrandCommerceFieldsProps {
  value: BrandCommerceForm;
  onChange: (next: BrandCommerceForm) => void;
  disabled?: boolean;
  /**
   * Which group to render. The brand form shows the storefront fields under
   * Commerce & Links and the founder under History & Status — one form value,
   * two places. Defaults to both.
   */
  part?: 'all' | 'storefront' | 'founder';
}

const inputClass =
  'w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500';
const checkboxClass = 'rounded border-gray-300 text-brand-600 focus:ring-brand-500';

export function BrandCommerceFields({ value, onChange, disabled = false, part = 'all' }: BrandCommerceFieldsProps) {
  const id = useId();
  const showStorefront = part !== 'founder';
  const showFounder = part !== 'storefront';
  return (
    <div className='space-y-4'>
      {showStorefront && (
        <>
          <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
            <div>
              <label htmlFor={`${id}-platform`} className='block text-sm font-medium text-gray-700 mb-1'>
                Ecommerce platform
              </label>
              <input
                type='text'
                id={`${id}-platform`}
                value={value.ecommercePlatform}
                disabled={disabled}
                onChange={(e) => onChange({ ...value, ecommercePlatform: e.target.value })}
                placeholder='shopify, woocommerce, wix, custom…'
                className={inputClass}
              />
              <p className='text-xs text-gray-500 mt-1'>Saved in lower case.</p>
            </div>
            <div>
              <label htmlFor={`${id}-instagram`} className='block text-sm font-medium text-gray-700 mb-1'>
                Instagram URL
              </label>
              <input
                type='url'
                id={`${id}-instagram`}
                value={value.instagramUrl}
                disabled={disabled}
                onChange={(e) => onChange({ ...value, instagramUrl: e.target.value })}
                placeholder='https://www.instagram.com/…'
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <div className='flex items-center gap-2'>
              <input
                type='checkbox'
                id={`${id}-shopify`}
                checked={value.isShopifyBrand === true}
                disabled={disabled}
                onChange={(e) => onChange({ ...value, isShopifyBrand: e.target.checked })}
                className={checkboxClass}
              />
              <label htmlFor={`${id}-shopify`} className='text-sm font-medium text-gray-700'>
                Shopify storefront
              </label>
            </div>
            <p className='text-xs text-gray-500 mt-1'>
              The brand&apos;s own website runs on Shopify. Separate from Shopify product import, which
              is set up under Import Sources.
            </p>
          </div>
        </>
      )}

      {showFounder && (
        <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
          <div>
            <label htmlFor={`${id}-founder`} className='block text-sm font-medium text-gray-700 mb-1'>
              Founder
            </label>
            <input
              type='text'
              id={`${id}-founder`}
              value={value.founderName}
              disabled={disabled}
              onChange={(e) => onChange({ ...value, founderName: e.target.value })}
              placeholder='Founder name(s)'
              className={inputClass}
            />
            {value.founderLastEditAt && (
              <p className='text-xs text-gray-500 mt-1'>Last founder edit: {value.founderLastEditAt}</p>
            )}
          </div>
          <div className='flex items-center gap-2 sm:pt-7'>
            <input
              type='checkbox'
              id={`${id}-founderManaged`}
              checked={value.founderManaged === true}
              disabled={disabled}
              onChange={(e) => onChange({ ...value, founderManaged: e.target.checked })}
              className={checkboxClass}
            />
            <label htmlFor={`${id}-founderManaged`} className='text-sm font-medium text-gray-700'>
              Founder-managed
            </label>
          </div>
        </div>
      )}
    </div>
  );
}
